import { asyncAction } from "../common"
import { limsRequest } from "./limsRequest"

// Batch / lot rejections (PRD-003 Area 4 / SPEC-010). When QA signs a `batch-rejected`
// decision for a sample, the WHOLE batch/lot that sample belongs to is blocked from sale —
// not just the one sample. The lot identity is `limsSampleQc.batchLot` (the F-C1 sample-QC
// detail). A hold is keyed BY the lot string, so the release gate can block every sample
// sharing that lot by a single lookup.
//
// Persisted via the real /lims/batchHolds endpoints (the standalone POST is a plain upsert;
// the ATOMIC qaDecisions path creates its hold server-side under the QA signature — SPEC-021
// B7). See docs/specs/lims-api-contract.md.
//
// Record shape (id === batchLot):
//   { id, batchLot, rejectedBy, rejectedAt, qaDecisionId, reason,
//     // PRD-006 K13 (b) — present once a signed QA lift-action has lifted the hold:
//     liftedBy, liftedAt, liftReason }
const initialState = { items: [], isLoading: false }

export function reducer(state = initialState, action) {
  switch (action.type) {
    case "limsBatchHold/fetch/BEGIN":
      return { ...state, isLoading: true }
    case "limsBatchHold/fetch":
      return { items: action.res, isLoading: false }
    case "limsBatchHold/hold":
    case "limsBatchHold/lift": {
      const exists = state.items.some((x) => x.id === action.res.id)
      const items = exists
        ? state.items.map((x) => (x.id === action.res.id ? action.res : x))
        : [...state.items, action.res]
      return { ...state, items }
    }
    case "auth/logout":
      return initialState
    default:
      return state
  }
}

export const fetch = asyncAction("limsBatchHold/fetch", (_dispatch) =>
  limsRequest("GET", "/lims/batchHolds").then((res) => res.items)
)

// Reject (hold) a batch/lot from sale. Keyed by the lot string (upsert): re-rejecting the same
// lot replaces the record. The server stamps rejectedBy (falling back to the current member) and
// rejectedAt and writes the limsAuditBatchRejected audit entry; only the reason travels from the
// QA signature (its password belongs to the SIGNED call that authorised the decision — e.g. the
// setQaDecision PATCH — never to this plain upsert). The atomic OOS-disposition path creates its
// hold server-side under the qaDecisions signature instead of calling this (SPEC-021 B7).
export const hold = asyncAction("limsBatchHold/hold", (_dispatch, batchLot, { qaDecisionId, signature, reason } = {}) => {
  if (!batchLot) return Promise.reject(new Error("limsBatchHold.hold: no batchLot"))
  const record = {
    id: batchLot,
    batchLot,
    qaDecisionId: qaDecisionId || null,
    reason: (signature && signature.reason) || reason || null,
  }
  return limsRequest("POST", "/lims/batchHolds", { input: record }).then((res) => res.item)
})

// PRD-006 K13 (b) — LIFT an active batch hold via a signed, reason-mandatory QA action. The original
// hold record is preserved (never deleted); the server stamps liftedBy/liftedAt/liftReason and the
// audit entry, after verifying the QA password (qa capability, 4-eyes ≠ the rejectedBy signer). A
// lifted hold no longer blocks release (selectIsBatchRejected below skips it). The signature's
// reason is MANDATORY (server 422s on a blank reason — the ESignatureModal flow mirrors this).
// Errors surfaced inline by the dialog: 404 (no hold), 409 (already lifted), 403 (cap / four-eyes).
export const lift = asyncAction("limsBatchHold/lift", (_dispatch, batchLot, signature = null) => {
  if (!batchLot) return Promise.reject(new Error("limsBatchHold.lift: no batchLot"))
  return limsRequest("PATCH", `/lims/batchHolds/${encodeURIComponent(batchLot)}/lift`, {
    password: (signature && signature.password) || undefined,
    reason: (signature && signature.reason) || undefined,
  }).then((res) => res.item)
})

// Is a batch hold record currently ACTIVE (placed and NOT yet lifted)? A signed QA lift (K13 b)
// stamps liftedAt, which neutralises the hold — so a lifted record is treated as no longer holding.
export const isHoldActive = (hold) => !!hold && !hold.liftedAt

// Is a given batch/lot string rejected (held from sale)? The release gate consults this BEFORE
// the open-OOS branch so a signed batch rejection is a distinct, terminal disposition. A hold that
// has been signed-off as LIFTED (K13 b — liftedAt set) no longer counts, so the gate re-evaluates
// to releasable once the lift lands.
export const selectIsBatchRejected = (state, batchLot) =>
  !!batchLot && state.limsBatchHold.items.some((x) => x.batchLot === batchLot && isHoldActive(x))

export const selectRejectedBatches = (state) => state.limsBatchHold.items

// Only the ACTIVE holds (not yet lifted) — the "active holds" table reads this so a lifted hold drops
// out of the actionable list (it still shows in the full register / "lifted" section).
export const selectActiveHolds = (state) => state.limsBatchHold.items.filter(isHoldActive)

// Only the LIFTED holds (a signed QA lift has neutralised them) — shown with a "Lifted" badge +
// liftedBy/At/reason so the correction is visible (GMP: the record persists, the chain is auditable).
export const selectLiftedHolds = (state) => state.limsBatchHold.items.filter((x) => !!x.liftedAt)
